/**
 * Whether hydration debugging is enabled via the `debugHydration` query param.
 * Only enabled in non-production environments.
 */
export function isDebugHydrationEnabled() {
  if (typeof window === 'undefined') return false;
  const isProd = process.env.NODE_ENV === 'production';
  const params = new URLSearchParams(window.location.search);
  return !isProd && params.has('debugHydration');
}

/**
 * Pretty-prints an HTML string by inserting newlines and indentation.
 * "Super simple" (not a full HTML parser) but robust enough for making `node.innerHTML`
 * more readable for diffing purposes.
 *
 * Using this instead of a library like `pretty` or `js-beautify` because we want to fully
 * understand that prettified output isn't affecting the SSR vs CSR diff.
 */
export function formatHtml(
  html: string,
  {
    indent = 2,
    newline = '\n',
    keepTags = new Set(['pre', 'textarea', 'script', 'style']),
  }: { indent?: number; newline?: string; keepTags?: Set<string> } = {},
): string {
  const voidTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  const IND = ' '.repeat(indent);
  let level = 0;
  const keepStack: string[] = [];
  const out: string[] = [];

  // Break into tags, comments/doctype, and text
  const tokens = html.match(/<!--[\s\S]*?-->|<!DOCTYPE[^>]*>|<\/?[a-zA-Z][^>]*>|[^<]+/g) || [];

  const isStartTag = (t: string) => /^<([a-zA-Z0-9:-]+)(\s[^>]*)?>$/.test(t);
  const isEndTag = (t: string) => /^<\/([a-zA-Z0-9:-]+)\s*>$/.test(t);
  const getTagName = (t: string) => {
    const m = /^<\/?\s*([a-zA-Z0-9:-]+)/.exec(t);
    return m ? m[1].toLowerCase() : '';
  };
  const isSelfClosingSyntax = (t: string) => /\/>\s*$/.test(t);

  tokens.forEach(token => {
    // If inside a keep-content tag (<pre>, <script>, etc.), we pass text verbatim.
    if (keepStack.length) {
      if (isEndTag(token)) {
        const name = getTagName(token);
        // closing a kept tag → dedent first
        level = Math.max(0, level - 1);
        out.push(IND.repeat(level) + token.trim());
        if (keepStack[keepStack.length - 1] === name) keepStack.pop();
      } else {
        // Keep verbatim text & tags inside (no reflow), but still newline-split for indentation on lines
        const lines = token.split(/\r?\n/);
        lines.forEach(line => {
          out.push(IND.repeat(level) + line);
        });
      }
      return;
    }

    // Outside kept tags:
    if (isEndTag(token)) {
      // Dedent before printing closing tags
      level = Math.max(0, level - 1);
      out.push(IND.repeat(level) + token.trim());
      return;
    }

    if (isStartTag(token)) {
      const name = getTagName(token);
      const isVoid = voidTags.has(name) || isSelfClosingSyntax(token);

      out.push(IND.repeat(level) + token.trim());

      if (!isVoid) {
        // If this is a tag whose content we keep verbatim, push to keepStack
        if (keepTags.has(name)) {
          keepStack.push(name);
          level += 1; // still indent its contents
        } else {
          level += 1;
        }
      }
      return;
    }

    if (/^<!/.test(token)) {
      // comment or doctype
      out.push(IND.repeat(level) + token.trim());
      return;
    }

    // Text node: collapse whitespace unless it's significant (we're not inside keep-tags here)
    const text = token.replace(/\s+/g, ' ').trim();
    if (text) out.push(IND.repeat(level) + text);
  });

  // Join, then clean up extra blank lines around block boundaries
  return out.join(newline).replace(/[ \t]+$/gm, '');
}
